import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { getQuestionsForLevel, completeLevel } from '../utils/api';
import { Clock, Award } from 'lucide-react';

const pointsPerLevel = {
  1: 10,        // Level 1: 10 members per correct answer
  2: 30,        // Level 2: 30 members per correct answer
  3: 30,        // Level 3: 30 members per correct answer
  4: 50,        // Premium Level 4: 50 members per correct answer
  5: 50,        // Premium Level 5: 50 members per correct answer
  6: 50         // Premium Level 6: 50 members per correct answer
};

const timerPerLevel = {
  1: 15, 2: 15, 3: 15,
  4: 15, 5: 15, 6: 15
};

export default function Gameplay({ user }) {
  const { level } = useParams();
  const navigate = useNavigate();
  
  // Shuffle function for Level 4 answers
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [membersGained, setMembersGained] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent double submission
  const [timeLeft, setTimeLeft] = useState(timerPerLevel[parseInt(level)] || 15);
  const [showFeedback, setShowFeedback] = useState(false);
  const [shuffledOptions, setShuffledOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const isGuest = localStorage.getItem('guest_mode') === 'true';
    if (!user && !isGuest) {
      toast.error('Please sign in first');
      navigate('/auth');
      return;
    }
    loadQuestions();
  }, [level]);

  useEffect(() => {
    if (questions.length === 0 || showFeedback) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [currentIndex, questions, showFeedback]);

  const loadQuestions = async () => {
    try {
      const response = await getQuestionsForLevel(parseInt(level));
      setQuestions(response.data.questions);
      
      // For Level 4, initialize shuffled options for the first question
      if (parseInt(level) === 4 && response.data.questions.length > 0) {
        setShuffledOptions(shuffleArray(response.data.questions[0].options));
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to load questions:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error(`Failed to load questions: ${error.response?.data?.detail || error.message}`);
      navigate('/level-select');
    }
  };

  const handleTimeUp = () => {
    if (!showFeedback) {
      setShowFeedback(true);
      
      // Check if this is the last question
      if (currentIndex + 1 >= questions.length) {
        // Prevent double submission
        if (isSubmitting) {
          console.log('Already submitting - ignoring handleTimeUp');
          return;
        }
        setIsSubmitting(true);
        
        // If last question and time up, finish with current score
        setTimeout(() => {
          setScore((finalScore) => {
            setMembersGained((finalMembers) => {
              const isGuest = localStorage.getItem('guest_mode') === 'true' && !user;
              
              console.log('=== HANDLE TIME UP - LEVEL COMPLETE ===');
              console.log('Submitting from handleTimeUp');
              
              if (isGuest || !user) {
                navigate('/level-complete', { 
                  state: { 
                    level: parseInt(level), 
                    score: finalScore, 
                    membersGained: finalMembers 
                  } 
                });
              } else {
                console.log('🔴 CALLING completeLevel API from handleTimeUp');
                completeLevel({
                  level: parseInt(level),
                  score: finalScore,
                  members_gained: finalMembers,
                }).then(() => {
                  console.log('✅ completeLevel API SUCCESS from handleTimeUp');
                  navigate('/level-complete', { 
                    state: { 
                      level: parseInt(level), 
                      score: finalScore, 
                      membersGained: finalMembers 
                    } 
                  });
                }).catch((error) => {
                  console.error('Failed to save progress:', error);
                  toast.error('Failed to save progress - continuing to level complete');
                  navigate('/level-complete', { 
                    state: { 
                      level: parseInt(level), 
                      score: finalScore, 
                      membersGained: finalMembers 
                    } 
                  });
                });
              }
              return finalMembers;
            });
            return finalScore;
          });
        }, 2500);
      } else {
        setTimeout(() => nextQuestion(), 2000);
      }
    }
  };

  const handleAnswer = (answer) => {
    if (showFeedback) return;

    setSelectedAnswer(answer);
    const currentQ = questions[currentIndex];
    const isCorrect = answer === currentQ.correct_answer;

    // Show feedback immediately for instant color change
    setShowFeedback(true);

    if (isCorrect) {
      const points = pointsPerLevel[parseInt(level)];
      setScore((prev) => prev + points);
      setMembersGained((prev) => prev + points);
    }
    
    // Check if this is the last question
    if (currentIndex + 1 >= questions.length) {
      // Prevent double submission
      if (isSubmitting) {
        console.log('Already submitting - ignoring handleAnswer');
        return;
      }
      setIsSubmitting(true);
      
      // If last question, wait longer to ensure state updates complete before finishing
      setTimeout(() => {
        // Use functional updates to ensure we have the latest values
        setScore((finalScore) => {
          setMembersGained((finalMembers) => {
            // CRITICAL DEBUG LOGGING
            console.log('=== GAMEPLAY COMPLETE DEBUG ===');
            console.log('Level:', level);
            console.log('Final Score:', finalScore);
            console.log('Final Members Calculated:', finalMembers);
            console.log('Points Per Level:', pointsPerLevel[level]);
            console.log('Submitting from handleAnswer');
            console.log('===============================');
            
            // Navigate with the final values
            const isGuest = localStorage.getItem('guest_mode') === 'true' && !user;
            
            if (isGuest || !user) {
              navigate('/level-complete', { 
                state: { 
                  level: parseInt(level), 
                  score: finalScore, 
                  membersGained: finalMembers 
                } 
              });
            } else {
              completeLevel({
                level: parseInt(level),
                score: finalScore,
                members_gained: finalMembers,
              }).then(() => {
                navigate('/level-complete', { 
                  state: { 
                    level: parseInt(level), 
                    score: finalScore, 
                    membersGained: finalMembers 
                  } 
                });
              }).catch((error) => {
                console.error('Failed to save progress:', error);
                toast.error('Failed to save progress - continuing to level complete');
                navigate('/level-complete', { 
                  state: { 
                    level: parseInt(level), 
                    score: finalScore, 
                    membersGained: finalMembers 
                  } 
                });
              });
            }
            return finalMembers;
          });
          return finalScore;
        });
      }, 4500); // Wait slightly longer to ensure all state updates complete
    } else {
      setTimeout(() => nextQuestion(), 4000);
    }
  };

  const nextQuestion = () => {
    if (currentIndex + 1 < questions.length) {
      setCurrentIndex((prev) => prev + 1);
      setSelectedAnswer(null);
      setShowFeedback(false);
      setTimeLeft(timerPerLevel[parseInt(level)] || 15);
      setIsSubmitting(false); // Reset submission flag for next question
      
      // For Level 4, shuffle the answer options
      if (parseInt(level) === 4 && questions[currentIndex + 1]) {
        setShuffledOptions(shuffleArray(questions[currentIndex + 1].options));
      }
    }
    // Remove finishLevel() call from here since we handle it in handleAnswer
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FFD700] via-[#4169E1] to-[#9370DB]">
        <div className="text-white text-2xl font-bold">Loading questions...</div>
      </div>
    );
  }

  const currentQ = questions[currentIndex];
  const progressPercent = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div 
      className="min-h-screen py-4 sm:py-8 md:py-12 px-3 sm:px-4 md:px-6 relative"
      data-testid="gameplay-page"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/d1c9p3vy_FreeVector-Modern-City-Vector-Art.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#4169E1]/40 via-[#9370DB]/40 to-[#FFD700]/30"></div>
      
      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header - Mobile Optimized */}
        <div className="bg-white/90 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 mb-4 sm:mb-8 shadow-2xl">
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <div className="flex items-center gap-1.5 sm:gap-2" data-testid="members-display">
              <Award className="w-5 h-5 sm:w-6 sm:h-6 text-[#FFD700]" />
              <span className="text-lg sm:text-xl md:text-2xl font-bold text-[#4169E1]">
                <span className="hidden xs:inline">Members: </span>{membersGained}
              </span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2" data-testid="timer-display">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-[#4169E1]" />
              <span className={`text-lg sm:text-xl md:text-2xl font-bold ${timeLeft <= 5 ? 'text-red-500' : 'text-[#4169E1]'}`}>
                {timeLeft}s
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-xs sm:text-sm text-gray-600">
              <span>Question {currentIndex + 1} of {questions.length}</span>
              <span>Level {level}</span>
            </div>
          </div>
        </div>

        {/* Question Card - Mobile Optimized */}
        <Card className="bg-white/95 backdrop-blur-lg p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl shadow-2xl mb-4 sm:mb-8">
          <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-[#4169E1] mb-6 sm:mb-8 text-center leading-snug" data-testid="question-text">
            {currentQ.question_text}
          </h2>

          <div className="space-y-3 sm:space-y-4">
            {((parseInt(level) === 4 ? shuffledOptions : currentQ.options) || []).map((option, idx) => {
              const isSelected = selectedAnswer === option;
              const isCorrect = option === currentQ.correct_answer;
              let buttonClass = 'bg-white hover:bg-gray-50 border-2 border-gray-300';

              if (showFeedback) {
                if (isSelected && isCorrect) {
                  buttonClass = 'bg-green-500 text-white border-green-600';
                } else if (isSelected && !isCorrect) {
                  buttonClass = 'bg-red-500 text-white border-red-600';
                }
              } else if (isSelected) {
                buttonClass = 'bg-[#4169E1] text-white border-[#4169E1]';
              }

              return (
                <Button
                  key={idx}
                  data-testid={`option-${idx}`}
                  onClick={() => handleAnswer(option)}
                  disabled={showFeedback}
                  className={`w-full min-h-[56px] sm:h-16 md:h-auto md:py-4 px-4 text-sm sm:text-base md:text-lg font-semibold rounded-xl sm:rounded-2xl ${buttonClass} transform active:scale-95 sm:hover:scale-105 transition-all touch-manipulation`}
                >
                  <span className="block leading-tight">{option}</span>
                </Button>
              );
            })}
          </div>

          {/* Feedback - Mobile Optimized */}
          {showFeedback && (
            <div className="mt-4 sm:mt-6 text-center">
              {selectedAnswer === currentQ.correct_answer ? (
                <div className="text-green-600 text-xl sm:text-2xl font-bold animate-bounce" data-testid="correct-feedback">
                  ✓ Correct! +{pointsPerLevel[parseInt(level)]} Members
                </div>
              ) : (
                <div className="text-red-600 text-xl sm:text-2xl font-bold" data-testid="incorrect-feedback">
                  ✗ Wrong
                </div>
              )}
            </div>
          )}
        </Card>

      </div>
    </div>
  );
}
